/**
 * Life4Earth V2 — Background Texture System
 *
 * Three tiers:
 *   1. Solid (default) — plain --bg-primary, no texture
 *   2. SVG patterns    — inline data-URI SVGs, zero network cost
 *   3. Hi-res images   — user-downloaded files stored in public/textures/
 *
 * Persistence: localStorage key 'l4e_bg_texture'
 * Application: CSS background-image on .l4e-shell via applyBackgroundTexture()
 */

// ── Texture identifiers ─────────────────────────────────────

export type SvgTextureId = 'svgHexGrid' | 'svgCircuit' | 'svgTopography';
/**
 * DEV-1474. Two hex-glow images, because "dark" is not one colour:
 *   hiresHexGlow     → hex-glow-dark.png  — near-black. Pairs with NINJA.
 *   hiresHexGlowBlue → hex-glow-blue.png  — blue-hued. Pairs with DARK, whose
 *                                           palette is slate/blue (SLATE[950]
 *                                           #020617), against which the black
 *                                           image reads as a muddy grey wash.
 * Both declare 'dark' compatibility, so both are offered on any dark-based
 * theme (including custom ones) and the user picks the one that suits.
 */
export type HiresTextureId = 'hiresHexGlow' | 'hiresHexGlowBlue';
export type TextureId = 'solid' | SvgTextureId | HiresTextureId;

export const SVG_TEXTURE_IDS: SvgTextureId[] = ['svgHexGrid', 'svgCircuit', 'svgTopography'];
export const HIRES_TEXTURE_IDS: HiresTextureId[] = ['hiresHexGlow', 'hiresHexGlowBlue'];
export const ALL_TEXTURE_IDS: TextureId[] = ['solid', ...SVG_TEXTURE_IDS, ...HIRES_TEXTURE_IDS];

// ── Theme compatibility ──────────────────────────────────────
// Each texture declares which base themes it supports.
// 'solid' is compatible with everything.
// SVG patterns adapt their stroke colors per theme.
// Hi-res images are theme-specific (dark hex glow won't work on light).

export type BaseTheme = 'dark' | 'light';

/**
 * DEV-1478. Textures are declared per BUILT-IN THEME NAME, not per base theme.
 *
 * THE TRAP THIS AVOIDS. The built-in theme names are literally 'dark' and
 * 'light' — the same strings as the base names. So a list like ['dark'] is
 * ambiguous: does it mean "the theme called Dark" or "anything dark-based
 * (including Ninja)"? Keying strictly on theme NAME removes the ambiguity, and
 * custom themes are then handled by an explicit rule rather than by accident.
 *
 * BUILT-IN theme → offered a texture iff its name is in the list.
 * CUSTOM theme   → has no name in any list (its name is unpredictable), so it is
 *                  offered every texture allowed for ANY built-in sharing its
 *                  base. A custom dark theme therefore sees both hex glows,
 *                  because we cannot know which palette its author chose — and
 *                  offering nothing would be worse than offering both.
 */
const BUILTIN_THEME_BASE: Record<string, BaseTheme> = {
  dark: 'dark',
  light: 'light',
  ninja: 'dark',
};

const TEXTURE_THEME_COMPAT: Record<TextureId, string[]> = {
  // SVG patterns adapt their stroke colour per base, so they suit every theme.
  solid:            ['dark', 'light', 'ninja'],
  svgHexGrid:       ['dark', 'light', 'ninja'],
  svgCircuit:       ['dark', 'light', 'ninja'],
  svgTopography:    ['dark', 'light', 'ninja'],

  // The two hex glows are paired to the theme each was made for (Skip, 2026-07-13):
  //   Black → NINJA only. A near-black image on Ninja's near-black palette.
  //   Blue  → DARK only.  Dark's palette is slate/blue; the black image washes
  //                       out against it, which is what prompted the pairing.
  hiresHexGlow:     ['ninja'],
  hiresHexGlowBlue: ['dark'],
};

/** Every built-in theme, of the given base, that this texture is offered to. */
function allowsAnyBuiltinOfBase(id: TextureId, base: BaseTheme): boolean {
  const targets = TEXTURE_THEME_COMPAT[id] ?? [];
  return targets.some((name) => BUILTIN_THEME_BASE[name] === base);
}

/* ── Admin-configured overrides (DEV-1478) ──────────────────────
 *
 * The pairings above are the DEFAULTS. An admin can override them per theme in
 * the Theme Designer, and that choice is stored in the theme's existing
 * token_overrides bag under this reserved key — which is why this needed no
 * schema change and no new endpoint: token_overrides already round-trips
 * localStorage → API → theme_overrides.token_overrides (JSONB) for built-in AND
 * custom themes alike.
 *
 * The value is a comma-separated list of texture ids. An ABSENT key means "use
 * the defaults" — which is deliberately different from an EMPTY string, meaning
 * "this theme offers no textures but Solid". Absent ≠ empty; conflating them
 * would make it impossible to switch a theme back to defaults.
 *
 * The key is a `--`-prefixed CSS custom property so it survives the generic
 * token pipeline untouched. It lands inertly on :root, which also makes the
 * current setting inspectable in devtools.
 */
export const TEXTURE_ALLOWLIST_TOKEN = '--l4e-allowed-textures';

/** Parse the reserved token into a texture-id list. `null` = not configured. */
export function parseTextureAllowlist(raw: string | undefined | null): TextureId[] | null {
  if (raw == null) return null;                       // not configured → defaults
  const ids = raw.split(',').map((s) => s.trim()).filter(Boolean);
  return ids.filter((id): id is TextureId => (ALL_TEXTURE_IDS as string[]).includes(id));
}

export function serializeTextureAllowlist(ids: TextureId[]): string {
  return ids.join(',');
}

/**
 * The textures on offer for the active theme.
 *
 * Resolution order:
 *   1. An admin allowlist configured for this theme in the Theme Designer.
 *   2. Otherwise the built-in defaults above.
 * Solid is always included — a theme with no background is always a valid choice,
 * and letting an admin remove it would leave a user unable to turn a texture off.
 *
 * @param baseTheme  The theme's resolved base — 'dark' or 'light'.
 * @param themeName  The theme's own name. Optional: when omitted the call
 *                   degrades to base-level matching, which is what every
 *                   pre-DEV-1478 caller expects.
 * @param allowlist  DEV-1478. The admin-configured list, or null/undefined for
 *                   "not configured → use defaults". Passed in rather than read
 *                   here, so this module stays free of storage dependencies.
 */
export function texturesForTheme(
  baseTheme: BaseTheme,
  themeName?: string,
  allowlist?: TextureId[] | null,
): TextureId[] {
  return ALL_TEXTURE_IDS.filter((id) => isTextureCompatible(id, baseTheme, themeName, allowlist));
}

/** Is this texture on offer for the active theme? */
export function isTextureCompatible(
  id: TextureId,
  baseTheme: BaseTheme,
  themeName?: string,
  allowlist?: TextureId[] | null,
): boolean {
  // Solid is never restrictable — see above.
  if (id === 'solid') return true;

  // DEV-1478 — an explicit admin allowlist wins over the defaults entirely.
  if (allowlist) return allowlist.includes(id);

  const targets = TEXTURE_THEME_COMPAT[id];
  if (!targets) return false;

  // Built-in theme: an exact, deliberate pairing.
  if (themeName && BUILTIN_THEME_BASE[themeName]) {
    return targets.includes(themeName);
  }

  // Custom theme (or an unnamed call): inherit whatever any built-in on the same
  // base is allowed. This is what keeps custom themes from being starved down to
  // Solid, and it is why the map has to know each built-in's base.
  return allowsAnyBuiltinOfBase(id, baseTheme);
}

// ── Hi-res download registry ─────────────────────────────────

export interface HiresTextureInfo {
  id: HiresTextureId;
  filename: string;
  /** URL to download from (CDN or repo-hosted) */
  downloadUrl: string;
  /** Local path under public/ after download */
  localPath: string;
  /** Approximate file size for UI display */
  sizeLabel: string;
}

export const HIRES_TEXTURES: Record<HiresTextureId, HiresTextureInfo> = {
  // "Hex Glow (Black)" — the original. The filename stays hex-glow-dark.png:
  // renaming the asset would break the preference of every user who already
  // selected it (the id is what's persisted, but the file is what's fetched).
  hiresHexGlow: {
    id: 'hiresHexGlow',
    filename: 'hex-glow-dark.png',
    downloadUrl: '/textures/hex-glow-dark.png', // served from public/textures/
    localPath: '/textures/hex-glow-dark.png',
    sizeLabel: '~1 MB',
  },
  // DEV-1474 — "Hex Glow (Blue)". The Dark theme's palette is slate/blue, so the
  // black image reads as a grey wash against it; this one is hued to match.
  hiresHexGlowBlue: {
    id: 'hiresHexGlowBlue',
    filename: 'hex-glow-blue.png',
    downloadUrl: '/textures/hex-glow-blue.png',
    localPath: '/textures/hex-glow-blue.png',
    sizeLabel: '~1 MB',
  },
};

// ── SVG pattern generators ───────────────────────────────────
// Each returns a data-URI SVG string. Patterns adapt stroke colors
// based on the active base theme (dark = light strokes, light = dark strokes).

function svgToDataUri(svg: string): string {
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

// Stroke color palettes per theme
const STROKE = {
  dark:  { primary: 'rgba(148,163,184,0.06)', secondary: 'rgba(148,163,184,0.04)', tertiary: 'rgba(148,163,184,0.03)' },
  light: { primary: 'rgba(30,41,59,0.07)',    secondary: 'rgba(30,41,59,0.05)',    tertiary: 'rgba(30,41,59,0.035)' },
};

const SVG_PATTERNS: Record<SvgTextureId, (theme: BaseTheme) => string> = {
  svgHexGrid: (theme) => { const s = STROKE[theme]; return svgToDataUri(`<svg xmlns="http://www.w3.org/2000/svg" width="56" height="100">
  <path d="M28 66L0 50L0 16L28 0L56 16L56 50L28 66L28 100" fill="none" stroke="${s.primary}" stroke-width="0.8"/>
  <path d="M28 0L28 34L0 50L0 84L28 100L56 84L56 50L28 34" fill="none" stroke="${s.tertiary}" stroke-width="0.4"/>
</svg>`); },

  svgCircuit: (theme) => { const s = STROKE[theme]; return svgToDataUri(`<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80">
  <line x1="0" y1="40" x2="80" y2="40" stroke="${s.secondary}" stroke-width="0.6"/>
  <line x1="40" y1="0" x2="40" y2="80" stroke="${s.secondary}" stroke-width="0.6"/>
  <line x1="0" y1="0" x2="20" y2="20" stroke="${s.tertiary}" stroke-width="0.5"/>
  <line x1="60" y1="60" x2="80" y2="80" stroke="${s.tertiary}" stroke-width="0.5"/>
  <circle cx="40" cy="40" r="2" fill="${s.primary}"/>
  <circle cx="0" cy="0" r="1.5" fill="${s.secondary}"/>
  <circle cx="80" cy="0" r="1.5" fill="${s.secondary}"/>
  <circle cx="0" cy="80" r="1.5" fill="${s.secondary}"/>
  <circle cx="80" cy="80" r="1.5" fill="${s.secondary}"/>
  <line x1="20" y1="20" x2="40" y2="20" stroke="${s.tertiary}" stroke-width="0.5"/>
  <line x1="40" y1="60" x2="60" y2="60" stroke="${s.tertiary}" stroke-width="0.5"/>
</svg>`); },

  svgTopography: (theme) => { const s = STROKE[theme]; return svgToDataUri(`<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120">
  <path d="M10 60 Q30 20 60 40 T110 30" fill="none" stroke="${s.secondary}" stroke-width="0.7"/>
  <path d="M0 90 Q40 60 80 75 T120 55" fill="none" stroke="${s.tertiary}" stroke-width="0.6"/>
  <path d="M5 15 Q35 40 65 20 T115 45" fill="none" stroke="${s.tertiary}" stroke-width="0.5"/>
  <path d="M0 110 Q25 95 55 105 T120 90" fill="none" stroke="${s.secondary}" stroke-width="0.6"/>
</svg>`); },
};

// ── Persistence ──────────────────────────────────────────────

const STORAGE_KEY = 'l4e_bg_texture';

export function readTexturePreference(): TextureId {
  try {
    const val = localStorage.getItem(STORAGE_KEY);
    if (val && ALL_TEXTURE_IDS.includes(val as TextureId)) return val as TextureId;
  } catch { /* SSR or blocked storage */ }
  return 'solid';
}

export function saveTexturePreference(id: TextureId): void {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch { /* ignore */ }
}

// ── Hi-res texture availability ──────────────────────────────

const HIRES_DOWNLOADED_KEY = 'l4e_bg_hires_downloaded';

function readDownloadedSet(): Set<string> {
  try {
    const raw = localStorage.getItem(HIRES_DOWNLOADED_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch { return new Set(); }
}

function saveDownloadedSet(s: Set<string>): void {
  try {
    localStorage.setItem(HIRES_DOWNLOADED_KEY, JSON.stringify([...s]));
  } catch { /* ignore */ }
}

export function isHiresDownloaded(id: HiresTextureId): boolean {
  return readDownloadedSet().has(id);
}

export function markHiresDownloaded(id: HiresTextureId): void {
  const s = readDownloadedSet();
  s.add(id);
  saveDownloadedSet(s);
}

/**
 * Check if a hi-res texture file is actually accessible.
 * Uses a HEAD request to avoid downloading the full image.
 */
export async function probeHiresTexture(id: HiresTextureId): Promise<boolean> {
  const info = HIRES_TEXTURES[id];
  if (!info) return false;
  try {
    const resp = await fetch(info.localPath, { method: 'HEAD' });
    if (resp.ok) {
      markHiresDownloaded(id);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

// ── Application ──────────────────────────────────────────────

/** Active MutationObserver for deferred shell application (cleaned up when shell appears). */
let _pendingObserver: MutationObserver | null = null;

/**
 * Core texture application logic — assumes shell element exists.
 */
function applyToShell(shell: HTMLElement, id: TextureId, baseTheme: BaseTheme): void {
  if (id === 'solid') {
    shell.style.backgroundImage = '';
    shell.style.backgroundSize = '';
    shell.style.backgroundRepeat = '';
    shell.style.backgroundPosition = '';
    return;
  }

  // SVG patterns — pass theme for adaptive stroke colors
  if (SVG_TEXTURE_IDS.includes(id as SvgTextureId)) {
    const gen = SVG_PATTERNS[id as SvgTextureId];
    shell.style.backgroundImage = gen(baseTheme);
    shell.style.backgroundSize = '';    // natural tile size
    shell.style.backgroundRepeat = 'repeat';
    shell.style.backgroundPosition = '';
    return;
  }

  // Hi-res textures
  if (HIRES_TEXTURE_IDS.includes(id as HiresTextureId)) {
    const info = HIRES_TEXTURES[id as HiresTextureId];
    shell.style.backgroundImage = `url("${info.localPath}")`;
    shell.style.backgroundSize = 'cover';
    shell.style.backgroundRepeat = 'no-repeat';
    shell.style.backgroundPosition = 'center center';
    return;
  }
}

/**
 * Apply background texture to the .l4e-shell element.
 * Called from ThemeProvider on mount and when texture or theme changes.
 *
 * If .l4e-shell is not yet in the DOM (e.g. user is on the login page),
 * a MutationObserver watches for it and applies the texture when it appears.
 * This ensures the preference persists across login/logout cycles.
 *
 * @param id       — texture identifier
 * @param baseTheme — 'dark' or 'light', used to select SVG stroke colors
 */
export function applyBackgroundTexture(id: TextureId, baseTheme: BaseTheme = 'dark'): void {
  // Clean up any pending observer from a previous call
  if (_pendingObserver) {
    _pendingObserver.disconnect();
    _pendingObserver = null;
  }

  const shell = document.querySelector('.l4e-shell') as HTMLElement | null;
  if (shell) {
    applyToShell(shell, id, baseTheme);
    return;
  }

  // Shell not in DOM yet — watch for it (handles post-login mount)
  _pendingObserver = new MutationObserver((_mutations, obs) => {
    const el = document.querySelector('.l4e-shell') as HTMLElement | null;
    if (el) {
      obs.disconnect();
      _pendingObserver = null;
      applyToShell(el, id, baseTheme);
    }
  });
  _pendingObserver.observe(document.body, { childList: true, subtree: true });
}

/**
 * Dispatch event when texture changes so components can react.
 */
export function dispatchTextureChanged(id: TextureId): void {
  window.dispatchEvent(new CustomEvent('l4e_bg_texture_changed', { detail: id }));
}
